import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isNativeApp } from '../../../../core/util/device';
import IconWrapper from './IconWrapper';
import { colors } from '../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../constants/text';
import { CDP_TOUCH_CLAIM_THIS_CLINIC } from '../../../../core/analytics/events';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { isSolvPartner } from '../../../../core/util/location';
import { CLAIM_PATH } from '../../../../constants';
import { BASE_URL } from '../../../../config';
import { CLAIMING_LOCATION, getCustomization } from '../../util/customization';

const Root = styled.div`
  position: relative;
  -webkit-font-smoothing: antialiased;
  font-size: ${fontSize.mediumSmall};
  font-family: ${fontFamily.book};
  color: ${colors.lightText};
  cursor: default;
`;

const HoverRoot = styled(Root)`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ClaimedIcon = styled.img`
  height: 10px;
  width: 10px;
  display: inline-block;
  padding-right: 4px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
  font-size: ${fontSize.small};
`;

const PromptContainer = styled.div`
  position: absolute;
  cursor: default;
  color: ${colors.squid};
  top: 100%;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 260px;
  visibility: hidden;
  opacity: 0;
  z-index: 10;
  transition: visibility 0ms linear 100ms, transform 100ms ease-in-out, opacity 100ms ease-in-out;
  transform: perspective(500px) rotateX(5deg) translateY(-10px);

  ${HoverRoot}:hover & {
    transition: visibility 100ms linear 0ms, transform 100ms ease-in-out, opacity 100ms ease-in-out;
    visibility: visible;
    opacity: 1;
    transform: perspective(500px) rotateX(0deg) translateY(0px);
  }
`;

const Key = styled.div`
  position: absolute;
  width: 7px;
  height: 7px;
  top: -5px;
  left: 49%;
  border: ${colors.borderLine} 1px solid;
  background-color: ${colors.white};
  border-bottom: none;
  border-right: none;
  z-index: 11;
  transform-origin: center center;
  transform: rotate(45deg);
`;

const Prompt = styled.div`
  position: relative;
  margin-top: 10px;
  border: ${colors.dorianGrey} 1px solid;
  cursor: default;
  color: ${colors.squid};
  border-radius: 4px;
  padding: 0 16px;
  background-color: ${colors.white};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
`;

const PromptHeader = styled.div`
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
  padding: 15px 0 0;
  cursor: default;
`;

const PromptSubText = styled.p`
  cursor: default;
`;

const LinkText = styled.span`
  color: ${colors.link};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default class ClaimClinic extends PureComponent {
  static propTypes = {
    location: PropTypes.object.isRequired,
    className: PropTypes.string,
    userAgent: PropTypes.string,
  };

  constructor(props) {
    super(props);
    const customization = getCustomization(CLAIMING_LOCATION, { location: this.props.location });
    this.claimedText = customization.claimedText;
    this.unclaimedText = customization.unclaimedText;
  }

  getIconUrl = () =>
    isSolvPartner(this.props.location)
      ? '/images/icons/Icon-Claimed.svg'
      : '/images/icons/Icon-Unclaimed.svg';

  getIconAlt = () => (isSolvPartner(this.props.location) ? 'Claimed' : 'Unclaimed');

  getCTA = () => (isSolvPartner(this.props.location) ? this.claimedText : this.unclaimedText);

  handleClaimClick = () => {
    const {
      location,
      location: { id, uclId },
    } = this.props;
    const claimUrl = `${BASE_URL}${CLAIM_PATH}`;
    if (!isSolvPartner(location)) {
      analyticsTrackEvent(CDP_TOUCH_CLAIM_THIS_CLINIC, {
        locationId: id,
        uclId,
      });
      window.open(claimUrl);
    }
  };

  render = () => {
    const { className, location, userAgent } = this.props;
    if (isNativeApp(userAgent) && !isSolvPartner(location)) return null;

    const RootTag = isSolvPartner(location) ? Root : HoverRoot;
    return (
      <RootTag onClick={this.handleClaimClick}>
        <IconWrapper className={className}>
          <ClaimedIcon src={this.getIconUrl()} alt={this.getIconAlt()} />
          <NoWrap>{this.getCTA()}</NoWrap>
        </IconWrapper>
        <PromptContainer>
          <Prompt>
            <Key />
            <PromptHeader>
              This clinic hasn't been claimed by the owner or a representative yet.
            </PromptHeader>
            <PromptSubText>
              <LinkText>Claim this clinic</LinkText> to edit business details and business hours.
            </PromptSubText>
          </Prompt>
        </PromptContainer>
      </RootTag>
    );
  };
}
